import React, { useState } from 'react';
import { renderInFragment } from '../Utilities/WorkflowUIUtilities';
import { fixInput, GATEWAY_ERROR, GENERIC_ERROR, mergeServerErrors, nonInputErrorMessages } from '../Utilities/WorkflowInputUtilities';
import { getToken } from '../PaymentGateway';
import { divAttributeForScriptWithKey, payloadFromScript } from './DebitCardStepScriptParser';
export const isDebitCardFormStep = ({ workflowName, stepName, script }) => ((workflowName === 'make_payment' || workflowName === 'make_card_payment' || workflowName === 'manage_saved_debit_card') &&
    (stepName === 'card_information' || stepName === 'get_card_information_and_save_card') &&
    divAttributeForScriptWithKey(script, 'data-react-class') === 'CreditCardForm');
const validateInput = ({ value, required, pattern }) => {
    const allowsAnyValue = !!(!pattern && value);
    const valueFitsPattern = !!(pattern && value && RegExp(pattern)
        .test(value));
    return !required || allowsAnyValue || valueFitsPattern;
};
const defaultError = 'Please enter valid debit card information';
const textInputBase = {
    __typename: 'WorkflowTextInput',
    value: null,
    required: true,
    disabled: false,
    valid: validateInput,
    keyboard: 'numeric',
    format: null
};
const checkboxInputBase = {
    __typename: 'WorkflowCheckboxInput',
    value: 'false',
    pattern: '^true$',
    valid: validateInput
};
const cardNumber = {
    ...textInputBase,
    name: 'debit_card_number',
    title: 'Debit Card Number',
    placeholder: 'Debit Card Number',
    pattern: '^[0-9]{12,16}$',
    errorMessage: 'Please enter a valid Debit Card Number',
    format: 'cardNumber'
};
const securityCode = {
    ...textInputBase,
    name: 'security_code',
    title: 'Security Code',
    placeholder: 'Security Code',
    pattern: '^[0-9]{3,4}$',
    errorMessage: 'Please enter a valid Security Code'
};
const zip = {
    ...textInputBase,
    name: 'zip_code',
    title: 'Zip',
    placeholder: 'Zip',
    pattern: '^[0-9]{5}(?:-[0-9]{4})?$',
    errorMessage: 'Please enter a valid Zip',
};
const expirationDate = {
    ...textInputBase,
    name: 'expiration_date',
    title: 'Expiration Date (MM/YYYY)',
    placeholder: 'MM/YYYY',
    pattern: '^(0[1-9]|10|11|12)/20[0-9]{2}$',
    errorMessage: 'Please enter a valid Expiration Date',
    format: 'cardExpirationDate'
};
const saveCardConfirmation = {
    ...checkboxInputBase,
    required: false,
    value: 'false',
    name: 'save_card_confirmation',
    placeholder: 'Save this card on file to be used in the future.',
    errorMessage: 'error'
};
const cardOwnerConfirmation = {
    ...checkboxInputBase,
    required: true,
    value: 'false',
    name: 'card_owner_confirmation',
    placeholder: 'I confirm that the name on my debit card is the same as the name on my account.',
    errorMessage: 'In order to continue, you must agree to confirm that the name on your debit card is the same as the name on your account.'
};
export const debitCardFormInputs = (userName, canManageDebitCard) => {
    const name = {
        ...textInputBase,
        name: 'cardholder_name',
        title: 'Cardholder Name',
        placeholder: 'First Last',
        pattern: null,
        disabled: !!userName,
        value: userName || null,
        errorMessage: 'Please enter a valid Cardholder Name'
    };
    const baseInputs = [name, cardNumber, expirationDate, securityCode, zip, cardOwnerConfirmation];
    if (canManageDebitCard) {
        baseInputs.splice(-1, 0, saveCardConfirmation);
    }
    return baseInputs;
};
// tslint:disable-next-line: max-func-body-length
export const DebitCardStep = ({ uiprops, step, counter, updateCounter, inputRef }) => {
    const { renderStep, renderAction, renderInput, renderScript, runWorkflow, submitted } = uiprops;
    // fetch autofill for inputs from script data
    const scriptProps = payloadFromScript(step.script);
    const customerName = scriptProps && scriptProps.customerName || undefined;
    const canManageDebitCard = scriptProps && scriptProps.is_eligible_for_workflow_on_dash || undefined;
    // create inputs
    const formInputs = debitCardFormInputs(customerName, canManageDebitCard);
    // init values state and validation
    const initialInputMap = new Map(formInputs.map((input) => [input.name, input.value]));
    const validationData = new Map(formInputs
        .map((input) => [input.name, {
            pattern: input.pattern,
            errorMessage: input.errorMessage,
            required: input.required,
            valid: ({ value, required, pattern }) => input.valid({ value, required, pattern })
        }]));
    const [inputValues, setInputValues] = useState(new Map(initialInputMap));
    const [inputErrors, setInputErrors] = useState(new Map());
    const [stepErrors, setStepErrors] = useState(new Map());
    const [getTokenLoading, setGetTokenLoading] = useState(false);
    let formRef = null;
    const validateInputs = () => {
        if (!formRef || (!formRef.reportValidity())) {
            return false;
        }
        let errors = new Map();
        inputValues.forEach((value, key) => {
            const currentValidation = validationData.get(key);
            if (!!currentValidation) {
                const { pattern, required, valid, errorMessage } = currentValidation;
                if (!valid({ value, required, pattern })) {
                    errors = errors.set(key, errorMessage || defaultError);
                }
            }
        });
        setInputErrors(errors);
        setStepErrors(errors);
        return errors.size === 0;
    };
    const submit = async (action, data) => {
        if (!validateInputs()) {
            return; // prevent submission for bad inputs
        }
        setGetTokenLoading(true);
        const tokenizedCard = await getToken(step, inputValues);
        setGetTokenLoading(false);
        if (tokenizedCard.error) {
            setStepErrors(new Map([[GATEWAY_ERROR, tokenizedCard.error]]));
            return; // prevent submission for failed tokenize
        }
        // tslint:disable: no-backbone-get-set-outside-model
        const cardDate = inputValues.get('expiration_date') || '/';
        const expirationMonth = cardDate.split('/')[0];
        const expirationYear = cardDate.split('/')[1];
        await runWorkflow({
            variables: {
                action,
                inputs: [
                    { name: 'credit_card_uuid', value: tokenizedCard.uuid },
                    { name: 'credit_card_token', value: tokenizedCard.token },
                    { name: 'credit_card_last_4', value: tokenizedCard.last4 },
                    { name: 'card_expiration_month', value: expirationMonth },
                    { name: 'card_expiration_year', value: expirationYear },
                    { name: 'card_brand', value: tokenizedCard.brand },
                    { name: 'card_type', value: tokenizedCard.cardType }
                ].concat(canManageDebitCard
                    ? { name: 'credit_card_recurring', value: inputValues.get('save_card_confirmation') || undefined }
                    : []),
                previousState: data.me.getWorkflow.state
            }
        });
        // tslint:enable: no-backbone-get-set-outside-model
        updateCounter(counter);
    };
    const back = async (action, data) => {
        try {
            await runWorkflow({
                variables: {
                    action,
                    inputs: [],
                    previousState: data.me.getWorkflow.state
                }
            });
        }
        catch (e) {
            setStepErrors(new Map([[GENERIC_ERROR, 'An error occurred']]));
        }
    };
    const node = renderStep({
        formRef: r => { formRef = r; },
        inputRef,
        name: step.name,
        id: `step${counter}`,
        actions: step.actions.map(action => renderInFragment({
            key: action.name,
            node: renderAction({
                action: {
                    ...action,
                    skipValidation: false
                },
                onClick: async () => {
                    const data = uiprops.data;
                    if (!data) {
                        throw new Error('Missing Previous State');
                    }
                    if (!action.secondary) {
                        await submit(action.name, data);
                    }
                    else {
                        await back(action.name, data);
                    }
                },
                submitted: submitted || getTokenLoading
            })
        })),
        inputs: formInputs
            .map(input => renderInFragment({
            key: input.name,
            node: renderInput({
                input: fixInput(input),
                updateInput: value => setInputValues(new Map(inputValues.set(input.name, value))),
                value: inputValues.get(input.name) || undefined,
                error: inputErrors.get(input.name)
            })
        })),
        script: renderScript({
            script: step.script,
        }),
        // tslint:disable-next-line: newline-per-chained-call
        error: stepErrors.values().next().value,
        nonInputErrors: mergeServerErrors(stepErrors, nonInputErrorMessages(step.errors)),
        errors: [],
        submitted: submitted || getTokenLoading,
        onExit: null
    });
    /**
     * `renderStep` returns a `React.Node`, which is not a compatible type to `React.Component`.
     * We do a little JSX fragment wrapper here to allow us to return our type.
     */
    return (React.createElement(React.Fragment, null, node));
};
/**
 * Returns a rendered `DebitCardStep` in a generic way, to be consumable by `IWFExternalProps`
 *
 * @param props render props
 * @returns a `DebitCardStep`
 */
export const renderDebitCardStep = props => React.createElement(DebitCardStep, props, null);
